import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, DatePicker, Form, Input, InputNumber, Radio, Row, Select, Spin } from "antd";
import { PictureOutlined, UploadOutlined } from "@ant-design/icons";
import { useHistory, useLocation, useParams } from "react-router-dom";
import moment from "moment";
import { useUploadDocument } from "hooks/upload-document";
import { notify } from "utils/helperFuncs";
import StateSwitch from "components/StateSwitch";
import {
  ANNOUNCEMENT_BAR_DISPLAY_TYPE,
  ANNOUNCEMENT_BAR_POSITION,
  BANNER_SCREEN_LABELS,
  BANNER_SCREENS,
  BANNER_SOURCE,
  BANNER_TYPE,
  BANNER_TYPE_LABELS,
  ICON_PRESETS,
  NAVIGATION_TYPE,
  POPUP_TRIGGER_DELAY_UNIT,
  POPUP_TRIGGER_TYPE,
  POPUP_TRIGGER_TYPE_LABELS,
  SELLER_SEGMENT_LABELS,
  SELLER_SEGMENT_TYPE,
} from "../../../core/constants";
import {
  getImageDimensions,
  hasLandscapeScreen,
  hasSquareScreen,
  validateBannerForm,
  validateBannerImageFile,
  validateImageDimensions,
} from "../../../core/utils";
import {
  mapBackendDetailToFormValues,
  mapCreateInputToBackend,
  mapUpdateInputToBackend,
} from "../../../data/apiAdapters";
import { useBannerDetail, useCreateBanner, useUpdateBanner } from "../../../data/hooks";
import AnnouncementBarPreview from "../../components/AnnouncementBarPreview";
import ApplicableProductsSection from "../../components/ApplicableProductsSection";
import BannerConfirmModal from "../../components/BannerConfirmModal";
import ColorPresetPicker from "../../components/ColorPresetPicker";
import NavigationTargetSelect from "../../components/NavigationTargetSelect";
import "../../styles/marketingBanner.scss";

const { RangePicker } = DatePicker;
const { Option } = Select;

function BannerFormPage() {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = Boolean(id);

  // Read URL query params
  const searchParams = new URLSearchParams(location.search);
  const typeParam = searchParams.get("type") || BANNER_TYPE.BANNER;
  const duplicateId = searchParams.get("duplicateId");

  const [formType, setFormType] = useState(typeParam);

  // If editing, load detail of `id`; if duplicating, load detail of `duplicateId`
  const targetFetchId = isEdit ? id : duplicateId;
  const { banner: fetchedBanner, loading: loadingBanner } = useBannerDetail(targetFetchId);

  // Mutations
  const { createBanner, loading: creating } = useCreateBanner();
  const { updateBanner, loading: updating } = useUpdateBanner();
  const { handleUpload, loading: uploading } = useUploadDocument();

  // Form State
  const [name, setName] = useState("");
  const [source, setSource] = useState(BANNER_SOURCE.MANUAL);
  const [timeRange, setTimeRange] = useState(null);
  const [sellerSegmentType, setSellerSegmentType] = useState(SELLER_SEGMENT_TYPE.ALL);
  const [applicableProductIDs, setApplicableProductIDs] = useState([]);
  const [applicableProducts, setApplicableProducts] = useState([]);
  const [screens, setScreens] = useState([BANNER_SCREENS.HOME]);

  // Navigation
  const [navigationType, setNavigationType] = useState(() => {
    if (!isEdit && !duplicateId && typeParam === BANNER_TYPE.POPUP) {
      return NAVIGATION_TYPE.PROMOTION_CAMPAIGN;
    }
    return NAVIGATION_TYPE.NONE;
  });
  const [navigationTargetID, setNavigationTargetID] = useState(null);
  const [navigationTargetName, setNavigationTargetName] = useState("");
  const [navigationURL, setNavigationURL] = useState("");

  // Default link điều hướng là CTKM khi thêm mới Popup
  useEffect(() => {
    if (!isEdit && !duplicateId && formType === BANNER_TYPE.POPUP) {
      setNavigationType((prev) => (prev === NAVIGATION_TYPE.NONE ? NAVIGATION_TYPE.PROMOTION_CAMPAIGN : prev));
    }
  }, [formType, isEdit, duplicateId]);

  // Media
  const [imageURL, setImageURL] = useState("");
  const [squareImageURL, setSquareImageURL] = useState("");

  // Popup fields
  const [popupTriggerType, setPopupTriggerType] = useState(POPUP_TRIGGER_TYPE.APP_OPEN_OR_SCREEN_ENTER);
  const [popupTriggerDelayValue, setPopupTriggerDelayValue] = useState(0);
  const [popupTriggerDelayUnit, setPopupTriggerDelayUnit] = useState("SECOND");
  const [displayDurationType, setDisplayDurationType] = useState("LIMITED");
  const [displayDurationSeconds, setDisplayDurationSeconds] = useState(5);
  const [maxImpressions, setMaxImpressions] = useState(5);
  const [stopAfterClicks, setStopAfterClicks] = useState(1);
  const [frequencyCount, setFrequencyCount] = useState(1);
  const [frequencyPeriodValue, setFrequencyPeriodValue] = useState(1);
  const [frequencyPeriod, setFrequencyPeriod] = useState("DAY");

  // Announcement Bar fields
  const [announcementBarPosition, setAnnouncementBarPosition] = useState(ANNOUNCEMENT_BAR_POSITION.TOP);
  const [announcementBarAllowClose, setAnnouncementBarAllowClose] = useState(true);
  const [announcementBarDisplayType, setAnnouncementBarDisplayType] = useState(ANNOUNCEMENT_BAR_DISPLAY_TYPE.TEXT);
  const [announcementBarMessage, setAnnouncementBarMessage] = useState("");
  const [announcementBarCtaText, setAnnouncementBarCtaText] = useState("");
  const [announcementBarIconMode, setAnnouncementBarIconMode] = useState("PRESET");
  const [announcementBarIconPresetCode, setAnnouncementBarIconPresetCode] = useState("truck");
  const [announcementBarIconURL, setAnnouncementBarIconURL] = useState("");
  const [announcementBarBackgroundMode, setAnnouncementBarBackgroundMode] = useState("PRESET");
  const [announcementBarColorPresetCode, setAnnouncementBarColorPresetCode] = useState("green_mint");
  const [announcementBarBackgroundColor, setAnnouncementBarBackgroundColor] = useState("#00a86b");
  const [announcementBarGradientFromColor, setAnnouncementBarGradientFromColor] = useState("#ff6a00");
  const [announcementBarGradientToColor, setAnnouncementBarGradientToColor] = useState("#ee0979");
  const [announcementBarGradientAngle, setAnnouncementBarGradientAngle] = useState(90);
  const [announcementBarTextColor, setAnnouncementBarTextColor] = useState("#ffffff");
  const [announcementBarIconColor, setAnnouncementBarIconColor] = useState("#ffffff");
  const [announcementBarButtonColor, setAnnouncementBarButtonColor] = useState("#ffffff");
  const [announcementBarButtonTextColor, setAnnouncementBarButtonTextColor] = useState("#ee0979");

  // Validation & Modal
  const [errors, setErrors] = useState({});
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);

  // Check if PROMOTION source (read-only for time & navigation per BE rules)
  const isPromotionSource = source === BANNER_SOURCE.PROMOTION;

  // Pre-fill form when data loaded (for edit or duplicate)
  useEffect(() => {
    if (fetchedBanner) {
      const v = mapBackendDetailToFormValues(fetchedBanner);
      if (v) {
        setName(isEdit ? v.name : `${v.name} (Bản sao)`);
        setFormType(v.type);
        setSource(v.source);
        setTimeRange(v.timeRange);
        setSellerSegmentType(v.sellerSegmentType);
        setApplicableProductIDs(v.applicableProductIDs || []);
        setApplicableProducts(v.applicableProducts || []);
        setScreens(v.screens || []);
        setImageURL(v.imageURL || "");
        setSquareImageURL(v.squareImageURL || "");
        setNavigationType(v.navigationType);
        setNavigationTargetID(v.navigationTargetID);
        setNavigationTargetName(v.navigationTargetName);
        setNavigationURL(v.navigationURL);

        // Popup
        if (v.popupTriggerType) setPopupTriggerType(v.popupTriggerType);
        if (v.popupTriggerDelayValue != null) setPopupTriggerDelayValue(v.popupTriggerDelayValue);
        if (v.popupTriggerDelayUnit) setPopupTriggerDelayUnit(v.popupTriggerDelayUnit);
        if (v.displayDurationType) setDisplayDurationType(v.displayDurationType);
        if (v.displayDurationSeconds != null) setDisplayDurationSeconds(v.displayDurationSeconds);
        if (v.maxImpressions != null) setMaxImpressions(v.maxImpressions);
        if (v.stopAfterClicks != null) setStopAfterClicks(v.stopAfterClicks);
        if (v.frequencyCount != null) setFrequencyCount(v.frequencyCount);
        if (v.frequencyPeriodValue != null) setFrequencyPeriodValue(v.frequencyPeriodValue);
        if (v.frequencyPeriod) setFrequencyPeriod(v.frequencyPeriod);

        // Announcement Bar
        if (v.announcementBarPosition) setAnnouncementBarPosition(v.announcementBarPosition);
        if (v.announcementBarAllowClose != null) setAnnouncementBarAllowClose(v.announcementBarAllowClose);
        if (v.announcementBarDisplayType) setAnnouncementBarDisplayType(v.announcementBarDisplayType);
        if (v.announcementBarMessage) setAnnouncementBarMessage(v.announcementBarMessage);
        if (v.announcementBarCtaText) setAnnouncementBarCtaText(v.announcementBarCtaText);
        if (v.announcementBarIconMode) setAnnouncementBarIconMode(v.announcementBarIconMode);
        if (v.announcementBarIconPresetCode) setAnnouncementBarIconPresetCode(v.announcementBarIconPresetCode);
        if (v.announcementBarIconURL) setAnnouncementBarIconURL(v.announcementBarIconURL);
        if (v.announcementBarBackgroundMode) setAnnouncementBarBackgroundMode(v.announcementBarBackgroundMode);
        if (v.announcementBarColorPresetCode) setAnnouncementBarColorPresetCode(v.announcementBarColorPresetCode);
        if (v.announcementBarBackgroundColor) setAnnouncementBarBackgroundColor(v.announcementBarBackgroundColor);
        if (v.announcementBarGradientFromColor) setAnnouncementBarGradientFromColor(v.announcementBarGradientFromColor);
        if (v.announcementBarGradientToColor) setAnnouncementBarGradientToColor(v.announcementBarGradientToColor);
        if (v.announcementBarGradientAngle != null) setAnnouncementBarGradientAngle(v.announcementBarGradientAngle);
        if (v.announcementBarTextColor) setAnnouncementBarTextColor(v.announcementBarTextColor);
        if (v.announcementBarIconColor) setAnnouncementBarIconColor(v.announcementBarIconColor);
        if (v.announcementBarButtonColor) setAnnouncementBarButtonColor(v.announcementBarButtonColor);
        if (v.announcementBarButtonTextColor) setAnnouncementBarButtonTextColor(v.announcementBarButtonTextColor);
      }
    }
  }, [fetchedBanner, isEdit]);

  // Image upload handler
  const handleUploadFile = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input value so same file can be selected again
    e.target.value = "";

    const validation = validateBannerImageFile(file);
    if (!validation.valid) {
      notify.warning({ message: validation.message });
      return;
    }

    // Validate kích thước ảnh 600 x 600 px cho Popup
    if (formType === BANNER_TYPE.POPUP || target === "popup") {
      try {
        const dimensions = await getImageDimensions(file);
        const dimValidation = validateImageDimensions(dimensions, 600, 600);
        if (!dimValidation.valid) {
          notify.warning({ message: dimValidation.message });
          setErrors((prev) => ({ ...prev, imageURL: dimValidation.message }));
          return;
        }
      } catch (err) {
        // Bỏ qua nếu môi trường không đọc được kích thước
      }
    }

    try {
      const res = await handleUpload({ files: [file] });
      const url = res?.[0]?.url;
      if (url) {
        if (target === "landscape" || target === "popup") {
          setImageURL(url);
          setErrors((prev) => {
            if (!prev.imageURL) return prev;
            const next = { ...prev };
            delete next.imageURL;
            return next;
          });
        } else if (target === "square") {
          setSquareImageURL(url);
          setErrors((prev) => {
            if (!prev.squareImageURL) return prev;
            const next = { ...prev };
            delete next.squareImageURL;
            return next;
          });
        } else if (target === "announcementBarIcon") {
          setAnnouncementBarIconURL(url);
        }
        notify.success({ message: "Tải ảnh lên thành công" });
      }
    } catch (err) {
      notify.error({ message: "Không thể tải ảnh lên" });
    }
  };

  // Reset targetID, targetName, url và lỗi điều hướng khi thay đổi loại link điều hướng
  const handleNavigationTypeChange = (newType) => {
    setNavigationType(newType);
    setNavigationTargetID(null);
    setNavigationTargetName("");
    setNavigationURL("");
    setErrors((prev) => {
      if (!prev.navigationTargetID && !prev.navigationURL) return prev;
      const next = { ...prev };
      delete next.navigationTargetID;
      delete next.navigationURL;
      return next;
    });
  };

  // Compile current values object
  const currentValues = useMemo(() => {
    return {
      name,
      timeRange,
      sellerSegmentType,
      applicableProductIDs,
      applicableProducts,
      screens,
      imageURL,
      squareImageURL,
      navigationType,
      navigationTargetID,
      navigationTargetName,
      navigationURL,
      popupTriggerType,
      popupTriggerDelayValue,
      popupTriggerDelayUnit,
      displayDurationType,
      displayDurationSeconds,
      maxImpressions,
      stopAfterClicks,
      frequencyCount,
      frequencyPeriodValue,
      frequencyPeriod,
      announcementBarPosition,
      announcementBarAllowClose,
      announcementBarDisplayType,
      announcementBarMessage,
      announcementBarCtaText,
      announcementBarIconMode,
      announcementBarIconPresetCode,
      announcementBarIconURL,
      announcementBarBackgroundMode,
      announcementBarColorPresetCode,
      announcementBarBackgroundColor,
      announcementBarGradientFromColor,
      announcementBarGradientToColor,
      announcementBarGradientAngle,
      announcementBarTextColor,
      announcementBarIconColor,
      announcementBarButtonColor,
      announcementBarButtonTextColor,
    };
  }, [
    name,
    timeRange,
    sellerSegmentType,
    applicableProductIDs,
    applicableProducts,
    screens,
    imageURL,
    squareImageURL,
    navigationType,
    navigationTargetID,
    navigationTargetName,
    navigationURL,
    popupTriggerType,
    popupTriggerDelayValue,
    popupTriggerDelayUnit,
    displayDurationType,
    displayDurationSeconds,
    maxImpressions,
    stopAfterClicks,
    frequencyCount,
    frequencyPeriodValue,
    frequencyPeriod,
    announcementBarPosition,
    announcementBarAllowClose,
    announcementBarDisplayType,
    announcementBarMessage,
    announcementBarCtaText,
    announcementBarIconMode,
    announcementBarIconPresetCode,
    announcementBarIconURL,
    announcementBarBackgroundMode,
    announcementBarColorPresetCode,
    announcementBarBackgroundColor,
    announcementBarGradientFromColor,
    announcementBarGradientToColor,
    announcementBarGradientAngle,
    announcementBarTextColor,
    announcementBarIconColor,
    announcementBarButtonColor,
    announcementBarButtonTextColor,
  ]);

  // Form submit trigger
  const handlePreSubmit = () => {
    const errs = validateBannerForm(currentValues, formType, source);
    if (errors.imageURL && errors.imageURL.includes("600 x 600")) {
      errs.imageURL = errors.imageURL;
    }
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      notify.error({ message: errs[firstKey] });
      return;
    }
    setConfirmModalVisible(true);
  };

  // Actual submit to backend
  const handleConfirmSubmit = async () => {
    try {
      if (isEdit) {
        const input = mapUpdateInputToBackend(currentValues, formType, source);
        await updateBanner(id, input);
      } else {
        const input = mapCreateInputToBackend(currentValues, formType);
        await createBanner(input);
      }
      setConfirmModalVisible(false);
      history.push("/marketing-banner");
    } catch (err) {
      // Notification handled in hook
    }
  };

  // Add selected products from modal
  const handleAddProducts = (newProducts) => {
    const combined = [...applicableProducts];
    const newIds = new Set(applicableProductIDs);

    newProducts.forEach((p) => {
      if (!newIds.has(p.id)) {
        combined.push(p);
        newIds.add(p.id);
      }
    });

    setApplicableProducts(combined);
    setApplicableProductIDs(Array.from(newIds));
  };

  // Remove product from table
  const handleRemoveProduct = (prodId) => {
    setApplicableProducts((prev) => prev.filter((p) => p.id !== prodId));
    setApplicableProductIDs((prev) => prev.filter((pid) => pid !== prodId));
  };

  const typeLabel = BANNER_TYPE_LABELS[formType] || formType;
  const pageTitle = isEdit ? `Cập nhật ${typeLabel}` : `Tạo mới ${typeLabel}`;

  if (targetFetchId && loadingBanner) {
    return (
      <div className="marketing-banner-container" style={{ textAlign: "center", paddingTop: "100px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="marketing-banner-container">
      {/* Header */}
      <div className="banner-page-header">
        <div className="banner-breadcrumb">
          <span className="breadcrumb-link" onClick={() => history.push("/marketing-banner")}>
            Quản lý công cụ truyền thông
          </span>{" "}
          / {pageTitle}
        </div>
        <h1 className="banner-page-title">{pageTitle}</h1>
      </div>

      <Form layout="vertical">
        {/* ========================================================================= */}
        {/* 1. LAYOUT CHO TẠO MỚI / CẬP NHẬT BANNER                                   */}
        {/* ========================================================================= */}
        {formType === BANNER_TYPE.BANNER && (
          <Row gutter={20}>
            {/* Cột trái: Thông tin chung */}
            <Col span={12}>
              <div className="banner-section-card">
                <div className="banner-section-header">Thông tin chung</div>
                <div className="banner-section-body">
                  <Form.Item label="Tên banner *" validateStatus={errors.name ? "error" : undefined} help={errors.name}>
                    <Input
                      placeholder="Nhập tên banner"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={isPromotionSource}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Thời gian diễn ra *"
                    validateStatus={errors.timeRange ? "error" : undefined}
                    help={errors.timeRange}
                  >
                    <RangePicker
                      showTime={{ format: "HH:mm" }}
                      format="DD/MM/YYYY HH:mm"
                      value={timeRange}
                      onChange={(dates) => setTimeRange(dates)}
                      disabled={isPromotionSource}
                      style={{ width: "100%" }}
                      disabledDate={(current) => current && current < moment().startOf("day")}
                    />
                  </Form.Item>

                  <Form.Item label="Kênh hiển thị *">
                    <Select value="APP_SELLER" disabled style={{ width: "100%" }}>
                      <Option value="APP_SELLER">App đại lý</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Khách hàng mục tiêu *"
                    validateStatus={errors.sellerSegmentType ? "error" : undefined}
                    help={errors.sellerSegmentType}
                  >
                    <Select
                      placeholder="Chọn khách hàng mục tiêu"
                      value={sellerSegmentType}
                      onChange={(val) => setSellerSegmentType(val)}
                      disabled={isPromotionSource}
                      style={{ width: "100%" }}
                    >
                      {Object.entries(SELLER_SEGMENT_LABELS).map(([k, v]) => (
                        <Option key={k} value={k}>
                          {v}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Màn hình hiển thị *"
                    validateStatus={errors.screens ? "error" : undefined}
                    help={errors.screens}
                  >
                    <Select
                      mode="multiple"
                      placeholder="Chọn vị trí màn hình hiển thị"
                      value={screens}
                      onChange={(vals) => setScreens(vals)}
                      style={{ width: "100%" }}
                    >
                      {Object.entries(BANNER_SCREEN_LABELS).map(([k, v]) => (
                        <Option key={k} value={k}>
                          {v}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <div style={{ marginTop: "12px", fontSize: "12px", color: "#e02020", fontStyle: "italic" }}>
                    Lưu ý: Ảnh tải lên cho 3 màn Welcome, Tra cứu bảo hành và Tin nhắn bắt buộc phải có kích thước vuông
                    1500 x 1500 px. Các màn còn lại bắt buộc kích thước ngang 1800x600 px.
                  </div>
                </div>
              </div>
            </Col>

            {/* Cột phải: Thông tin Marketing & Hình ảnh */}
            <Col span={12}>
              <div className="banner-section-card">
                <div className="banner-section-header">Thông tin Marketing</div>
                <div className="banner-section-body">
                  <Form.Item label="Link điều hướng : *">
                    <NavigationTargetSelect
                      navigationType={navigationType}
                      targetID={navigationTargetID}
                      targetName={navigationTargetName}
                      url={navigationURL}
                      onTypeChange={handleNavigationTypeChange}
                      onTargetIDChange={(idVal, nameVal) => {
                        setNavigationTargetID(idVal);
                        setNavigationTargetName(nameVal);
                      }}
                      onURLChange={(u) => setNavigationURL(u)}
                      disabled={isPromotionSource}
                      error={{
                        targetID: errors.navigationTargetID,
                        url: errors.navigationURL,
                      }}
                    />
                  </Form.Item>

                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ fontWeight: 500, marginBottom: "6px", fontSize: "13px" }}>
                      Banner (định dạng 1800x600) {hasLandscapeScreen(screens) ? "*" : ""}
                    </div>
                    <div className="banner-upload-box">
                      {imageURL ? (
                        <div>
                          <img src={imageURL} alt="banner-1800x600" className="preview-img" />
                          <div>
                            <Button size="small" onClick={() => setImageURL("")}>
                              Thay ảnh khác
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <label style={{ cursor: "pointer", display: "block" }}>
                          <PictureOutlined style={{ fontSize: "32px", color: "#8c8c8c" }} />
                          <div style={{ marginTop: "8px", fontWeight: 500, color: "#2246dc" }}>
                            Nhấn để tải ảnh banner lên
                          </div>
                          <div className="upload-hint">
                            Kích thước chuẩn: 1800 x 600 px • Dung lượng ≤ 1MB (JPG, PNG) | ≤ 3MB (WEBP, GIF)
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={(e) => handleUploadFile(e, "landscape")}
                            disabled={uploading}
                          />
                        </label>
                      )}
                    </div>
                    {errors.imageURL && (
                      <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>{errors.imageURL}</div>
                    )}
                  </div>

                  <div>
                    <div style={{ fontWeight: 500, marginBottom: "6px", fontSize: "13px" }}>
                      Banner (định dạng 1500x1500) {hasSquareScreen(screens) ? "*" : ""}
                    </div>
                    <div className="banner-upload-box">
                      {squareImageURL ? (
                        <div>
                          <img
                            src={squareImageURL}
                            alt="banner-1500x1500"
                            className="preview-img"
                            style={{ maxHeight: "160px" }}
                          />
                          <div>
                            <Button size="small" onClick={() => setSquareImageURL("")}>
                              Thay ảnh khác
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <label style={{ cursor: "pointer", display: "block" }}>
                          <PictureOutlined style={{ fontSize: "32px", color: "#8c8c8c" }} />
                          <div style={{ marginTop: "8px", fontWeight: 500, color: "#2246dc" }}>
                            Nhấn để tải ảnh banner vuông lên
                          </div>
                          <div className="upload-hint">
                            Kích thước chuẩn: 1500 x 1500 px • Dung lượng ≤ 1MB (JPG, PNG) | ≤ 3MB (WEBP, GIF)
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={(e) => handleUploadFile(e, "square")}
                            disabled={uploading}
                          />
                        </label>
                      )}
                    </div>
                    {errors.squareImageURL && (
                      <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                        {errors.squareImageURL}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        )}

        {/* ========================================================================= */}
        {/* 2. LAYOUT CHO TẠO MỚI / CẬP NHẬT POPUP (Khớp 1-1 với Figma Image 2)       */}
        {/* ========================================================================= */}
        {formType === BANNER_TYPE.POPUP && (
          <>
            {/* Card 1: Thông tin Marketing */}
            <div className="banner-section-card">
              <div className="banner-section-header">Thông tin Marketing</div>
              <div className="banner-section-body">
                <Row gutter={24}>
                  {/* Cột trái */}
                  <Col span={13}>
                    <Form.Item
                      label="Tên popup *"
                      validateStatus={errors.name ? "error" : undefined}
                      help={errors.name}
                    >
                      <Input
                        placeholder="Nhập tên popup"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={isPromotionSource}
                      />
                    </Form.Item>

                    <Form.Item
                      label="Thời gian diễn ra *"
                      validateStatus={errors.timeRange ? "error" : undefined}
                      help={errors.timeRange}
                    >
                      <RangePicker
                        showTime={{ format: "HH:mm" }}
                        format="DD/MM/YYYY HH:mm"
                        value={timeRange}
                        onChange={(dates) => setTimeRange(dates)}
                        disabled={isPromotionSource}
                        style={{ width: "100%" }}
                        disabledDate={(current) => current && current < moment().startOf("day")}
                      />
                    </Form.Item>

                    <Form.Item label="Link điều hướng *">
                      <NavigationTargetSelect
                        navigationType={navigationType}
                        targetID={navigationTargetID}
                        targetName={navigationTargetName}
                        url={navigationURL}
                        onTypeChange={handleNavigationTypeChange}
                        onTargetIDChange={(idVal, nameVal) => {
                          setNavigationTargetID(idVal);
                          setNavigationTargetName(nameVal);
                        }}
                        onURLChange={(u) => setNavigationURL(u)}
                        disabled={isPromotionSource}
                        error={{
                          targetID: errors.navigationTargetID,
                          url: errors.navigationURL,
                        }}
                      />
                    </Form.Item>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Kênh hiển thị *">
                          <Select value="APP_SELLER" disabled style={{ width: "100%" }}>
                            <Option value="APP_SELLER">App đại lý</Option>
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          label="Khách hàng mục tiêu: *"
                          validateStatus={errors.sellerSegmentType ? "error" : undefined}
                          help={errors.sellerSegmentType}
                        >
                          <Select
                            placeholder="Chọn khách hàng mục tiêu"
                            value={sellerSegmentType}
                            onChange={(val) => setSellerSegmentType(val)}
                            disabled={isPromotionSource}
                            style={{ width: "100%" }}
                          >
                            {Object.entries(SELLER_SEGMENT_LABELS).map(([k, v]) => (
                              <Option key={k} value={k}>
                                {v}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>

                  {/* Cột phải: Pop-up Mobile (600 * 600 px) */}
                  <Col span={11}>
                    <div>
                      <div style={{ fontWeight: 500, marginBottom: "6px", fontSize: "13px" }}>
                        Pop-up Mobile (600 * 600 px) *
                      </div>
                      <div className="banner-upload-box" style={{ minHeight: "260px" }}>
                        {imageURL ? (
                          <div>
                            <img src={imageURL} alt="popup" className="preview-img" style={{ maxHeight: "240px" }} />
                            <div>
                              <Button
                                size="small"
                                onClick={() => {
                                  setImageURL("");
                                  setErrors((prev) => {
                                    if (!prev.imageURL) return prev;
                                    const next = { ...prev };
                                    delete next.imageURL;
                                    return next;
                                  });
                                }}
                              >
                                Thay ảnh khác
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <label style={{ cursor: "pointer", display: "block" }}>
                            <PictureOutlined style={{ fontSize: "36px", color: "#8c8c8c" }} />
                            <div style={{ marginTop: "8px", fontWeight: 500, color: "#2246dc" }}>
                              Nhấn để tải ảnh popup lên
                            </div>
                            <div className="upload-hint">
                              Kích thước chuẩn: 600 x 600 px • Dung lượng ≤ 1MB (JPG, PNG) | ≤ 3MB (WEBP, GIF)
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: "none" }}
                              onChange={(e) => handleUploadFile(e, "popup")}
                              disabled={uploading}
                            />
                          </label>
                        )}
                      </div>
                      {errors.imageURL && (
                        <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>{errors.imageURL}</div>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>
            </div>

            {/* Card 2: Điều kiện kích hoạt */}
            <div className="banner-section-card">
              <div className="banner-section-header">Điều kiện kích hoạt</div>
              <div className="banner-section-body">
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item label="Thời điểm kích hoạt: *">
                      <Select
                        value={popupTriggerType}
                        onChange={(val) => setPopupTriggerType(val)}
                        style={{ width: "100%" }}
                      >
                        {Object.entries(POPUP_TRIGGER_TYPE_LABELS).map(([k, v]) => (
                          <Option key={k} value={k}>
                            {v}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      label="Thời lượng hiển thị: *"
                      validateStatus={errors.displayDurationSeconds ? "error" : undefined}
                      help={errors.displayDurationSeconds}
                    >
                      <div style={{ display: "flex", width: "100%", gap: "8px" }}>
                        <Select
                          className={`popup-duration-type-select ${
                            displayDurationType === "LIMITED" ? "is-limited" : ""
                          }`}
                          value={displayDurationType}
                          onChange={(val) => {
                            setDisplayDurationType(val);
                            if (val === "UNLIMITED") {
                              setErrors((prev) => {
                                const next = { ...prev };
                                delete next.displayDurationSeconds;
                                return next;
                              });
                            } else if (
                              displayDurationSeconds == null ||
                              displayDurationSeconds === "" ||
                              displayDurationSeconds < 3 ||
                              displayDurationSeconds > 60
                            ) {
                              setErrors((prev) => ({
                                ...prev,
                                displayDurationSeconds: "Thời lượng hiển thị phải từ 3 đến 60 giây.",
                              }));
                            }
                          }}
                          style={{
                            width: displayDurationType === "LIMITED" ? "140px" : "100%",
                            flex: displayDurationType === "LIMITED" ? "none" : 1,
                          }}
                        >
                          <Option value="LIMITED">Có giới hạn</Option>
                          <Option value="UNLIMITED">Không giới hạn</Option>
                        </Select>

                        {displayDurationType === "LIMITED" && (
                          <div className="popup-duration-input-group" style={{ display: "flex", flex: 1, minWidth: 0 }}>
                            <InputNumber
                              value={displayDurationSeconds}
                              onChange={(val) => {
                                setDisplayDurationSeconds(val);
                                if (val == null || val === "" || val < 3 || val > 60) {
                                  setErrors((prev) => ({
                                    ...prev,
                                    displayDurationSeconds: "Thời lượng hiển thị phải từ 3 đến 60 giây.",
                                  }));
                                } else {
                                  setErrors((prev) => {
                                    if (!prev.displayDurationSeconds) return prev;
                                    const next = { ...prev };
                                    delete next.displayDurationSeconds;
                                    return next;
                                  });
                                }
                              }}
                              placeholder="Nhập số giây"
                              style={{
                                flex: 1,
                                minWidth: "70px",
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0,
                              }}
                            />
                            <span className="blue-unit-addon">giây</span>
                          </div>
                        )}
                      </div>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24} style={{ marginTop: "8px" }}>
                  <Col span={12}>
                    <Form.Item
                      label={
                        <div>
                          <span>Thời gian kích hoạt: *</span>{" "}
                          <span style={{ fontSize: "12px", color: "#8c8c8c", fontStyle: "italic" }}>
                            (Popup hiển thị sau)
                          </span>
                        </div>
                      }
                    >
                      <div style={{ display: "flex", gap: "8px" }}>
                        <InputNumber
                          min={0}
                          value={popupTriggerDelayValue}
                          onChange={(val) => setPopupTriggerDelayValue(val)}
                          style={{ flex: 1, minWidth: "80px" }}
                        />
                        <Select
                          className="popup-delay-unit-select"
                          value={popupTriggerDelayUnit}
                          onChange={(val) => setPopupTriggerDelayUnit(val)}
                          style={{ width: "120px", flex: "none" }}
                        >
                          {Object.entries(POPUP_TRIGGER_DELAY_UNIT).map(([k, v]) => (
                            <Option key={k} value={k}>
                              {v === "SECOND" ? "Giây" : v === "MINUTE" ? "Phút" : "Giờ"}
                            </Option>
                          ))}
                        </Select>
                      </div>
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      label="Màn hình hiển thị: *"
                      validateStatus={errors.screens ? "error" : undefined}
                      help={errors.screens}
                    >
                      <Select
                        placeholder="Chọn màn hiển thị"
                        value={screens[0] || undefined}
                        onChange={(val) => setScreens(val ? [val] : [])}
                        style={{ width: "100%" }}
                      >
                        {Object.entries(BANNER_SCREEN_LABELS).map(([k, v]) => (
                          <Option key={k} value={k} disabled={k === BANNER_SCREENS.WELCOME}>
                            {v}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </div>

            {/* Card 3: Vòng đời & tần suất hiển thị */}
            <div className="banner-section-card">
              <div className="banner-section-header">Vòng đời & tần suất hiển thị</div>
              <div className="banner-section-body">
                <Row gutter={24}>
                  {/* Cột trái */}
                  <Col span={11}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
                      <span style={{ width: "180px", fontWeight: 500, fontSize: "13px" }}>
                        Tổng số lần hiển thị/đại lý: *
                      </span>
                      <div style={{ flex: 1 }}>
                        <InputNumber
                          min={1}
                          value={maxImpressions}
                          onChange={(val) => setMaxImpressions(val)}
                          style={{ width: "100%" }}
                          placeholder="Nhập số lần hiển thị"
                        />
                        {errors.maxImpressions && (
                          <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "2px" }}>
                            {errors.maxImpressions}
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={{ width: "180px", fontWeight: 500, fontSize: "13px" }}>
                        Dừng hiển thị sau khi click vào popup: *
                      </span>
                      <div style={{ display: "flex", flex: 1 }}>
                        <InputNumber
                          min={1}
                          value={stopAfterClicks}
                          onChange={(val) => setStopAfterClicks(val)}
                          style={{
                            flex: 1,
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                          }}
                        />
                        <span className="blue-unit-addon">lần</span>
                      </div>
                    </div>
                  </Col>

                  {/* Cột phải: Tần suất hiển thị */}
                  <Col span={13} className="dotted-divider-col">
                    <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
                      <span style={{ width: "140px", fontWeight: 500, fontSize: "13px" }}>Tần suất hiển thị : *</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
                        <InputNumber
                          min={1}
                          value={frequencyCount}
                          onChange={(val) => setFrequencyCount(val)}
                          style={{ width: "60px" }}
                        />
                        <span style={{ color: "#595959", fontSize: "13px" }}>lần trong</span>
                        <InputNumber
                          min={1}
                          value={frequencyPeriodValue}
                          onChange={(val) => setFrequencyPeriodValue(val)}
                          style={{ width: "60px" }}
                        />
                        <Select
                          className="popup-frequency-period-select"
                          value={frequencyPeriod}
                          onChange={(val) => setFrequencyPeriod(val)}
                          style={{ width: "95px", flex: "none" }}
                        >
                          <Option value="DAY">Ngày</Option>
                          <Option value="WEEK">Tuần</Option>
                          <Option value="MONTH">Tháng</Option>
                        </Select>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </>
        )}

        {/* ========================================================================= */}
        {/* 3. LAYOUT CHO TẠO MỚI / CẬP NHẬT ANNOUNCEMENT BAR                          */}
        {/* ========================================================================= */}
        {formType === BANNER_TYPE.ANNOUNCEMENT_BAR && (
          <>
            {/* Card 1: Thông tin Marketing */}
            <div className="banner-section-card">
              <div className="banner-section-header">Thông tin Marketing</div>
              <div className="banner-section-body">
                <Row gutter={24}>
                  <Col span={13}>
                    <Form.Item
                      label="Tên Announcement Bar *"
                      validateStatus={errors.name ? "error" : undefined}
                      help={errors.name}
                    >
                      <Input
                        placeholder="Nhập tên announcement bar"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={isPromotionSource}
                      />
                    </Form.Item>

                    <Form.Item
                      label="Thời gian diễn ra *"
                      validateStatus={errors.timeRange ? "error" : undefined}
                      help={errors.timeRange}
                    >
                      <RangePicker
                        showTime={{ format: "HH:mm" }}
                        format="DD/MM/YYYY HH:mm"
                        value={timeRange}
                        onChange={(dates) => setTimeRange(dates)}
                        disabled={isPromotionSource}
                        style={{ width: "100%" }}
                        disabledDate={(current) => current && current < moment().startOf("day")}
                      />
                    </Form.Item>

                    <Form.Item label="Link điều hướng *">
                      <NavigationTargetSelect
                        navigationType={navigationType}
                        targetID={navigationTargetID}
                        targetName={navigationTargetName}
                        url={navigationURL}
                        onTypeChange={handleNavigationTypeChange}
                        onTargetIDChange={(idVal, nameVal) => {
                          setNavigationTargetID(idVal);
                          setNavigationTargetName(nameVal);
                        }}
                        onURLChange={(u) => setNavigationURL(u)}
                        disabled={isPromotionSource}
                        error={{
                          targetID: errors.navigationTargetID,
                          url: errors.navigationURL,
                        }}
                      />
                    </Form.Item>

                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Kênh hiển thị *">
                          <Select value="APP_SELLER" disabled style={{ width: "100%" }}>
                            <Option value="APP_SELLER">App đại lý</Option>
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          label="Khách hàng mục tiêu: *"
                          validateStatus={errors.sellerSegmentType ? "error" : undefined}
                          help={errors.sellerSegmentType}
                        >
                          <Select
                            placeholder="Chọn khách hàng mục tiêu"
                            value={sellerSegmentType}
                            onChange={(val) => setSellerSegmentType(val)}
                            disabled={isPromotionSource}
                            style={{ width: "100%" }}
                          >
                            {Object.entries(SELLER_SEGMENT_LABELS).map(([k, v]) => (
                              <Option key={k} value={k}>
                                {v}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>

                  {/* Cột phải: Live Preview */}
                  <Col span={11}>
                    <AnnouncementBarPreview
                      position={announcementBarPosition}
                      displayType={announcementBarDisplayType}
                      message={announcementBarMessage}
                      ctaText={announcementBarCtaText}
                      allowClose={announcementBarAllowClose}
                      iconMode={announcementBarIconMode}
                      iconPresetCode={announcementBarIconPresetCode}
                      iconURL={announcementBarIconURL}
                      backgroundMode={announcementBarBackgroundMode}
                      colorPresetCode={announcementBarColorPresetCode}
                      backgroundColor={announcementBarBackgroundColor}
                      gradientFromColor={announcementBarGradientFromColor}
                      gradientToColor={announcementBarGradientToColor}
                      gradientAngle={announcementBarGradientAngle}
                      textColor={announcementBarTextColor}
                      iconColor={announcementBarIconColor}
                      buttonColor={announcementBarButtonColor}
                      buttonTextColor={announcementBarButtonTextColor}
                      imageURL={imageURL}
                    />
                  </Col>
                </Row>
              </div>
            </div>

            {/* Card 2: Màn hình & hành vi hiển thị */}
            <div className="banner-section-card">
              <div className="banner-section-header">Màn hình & hành vi hiển thị</div>
              <div className="banner-section-body">
                <Form.Item
                  label="Màn hình hiển thị *"
                  validateStatus={errors.screens ? "error" : undefined}
                  help={errors.screens}
                >
                  <Select
                    mode="multiple"
                    placeholder="Chọn vị trí màn hình hiển thị"
                    value={screens}
                    onChange={(vals) => setScreens(vals)}
                    style={{ width: "100%" }}
                  >
                    {Object.entries(BANNER_SCREEN_LABELS).map(([k, v]) => (
                      <Option key={k} value={k}>
                        {v}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item label="Vị trí trên màn *">
                  <Radio.Group
                    value={announcementBarPosition}
                    onChange={(e) => setAnnouncementBarPosition(e.target.value)}
                  >
                    <Radio value={ANNOUNCEMENT_BAR_POSITION.TOP}>Đầu màn (dưới header)</Radio>
                    <Radio value={ANNOUNCEMENT_BAR_POSITION.BOTTOM}>Cuối màn (footer)</Radio>
                  </Radio.Group>
                </Form.Item>

                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "12px" }}>
                  <span style={{ fontWeight: 500, fontSize: "13px" }}>Cho phép đại lý đóng:</span>
                  <StateSwitch
                    checked={announcementBarAllowClose}
                    onChange={(checked) => setAnnouncementBarAllowClose(checked)}
                  />
                  <span style={{ fontSize: "12px", color: "#8c8c8c" }}>
                    Hiển thị nút "X" để đại lý đóng (tắt thanh này trên tất cả màn hình đến hết phiên/mở app lần
                    sau/thêm mới)
                  </span>
                </div>
              </div>
            </div>

            {/* Card 3: Nội dung hiển thị */}
            <div className="banner-section-card">
              <div className="banner-section-header">Nội dung hiển thị</div>
              <div className="banner-section-body">
                <Form.Item label="Dạng hiển thị *">
                  <Radio.Group
                    value={announcementBarDisplayType}
                    onChange={(e) => setAnnouncementBarDisplayType(e.target.value)}
                  >
                    <Radio value={ANNOUNCEMENT_BAR_DISPLAY_TYPE.TEXT}>Text + Icon + CTA</Radio>
                    <Radio value={ANNOUNCEMENT_BAR_DISPLAY_TYPE.IMAGE}>Ảnh</Radio>
                  </Radio.Group>
                </Form.Item>

                {announcementBarDisplayType === ANNOUNCEMENT_BAR_DISPLAY_TYPE.IMAGE ? (
                  <div style={{ maxWidth: "500px" }}>
                    <div style={{ fontWeight: 500, marginBottom: "6px", fontSize: "13px" }}>
                      Ảnh Announcement Bar (1125 x 90 px) *
                    </div>
                    <div className="banner-upload-box">
                      {imageURL ? (
                        <div>
                          <img src={imageURL} alt="ann-bar" className="preview-img" />
                          <div>
                            <Button size="small" onClick={() => setImageURL("")}>
                              Thay ảnh khác
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <label style={{ cursor: "pointer", display: "block" }}>
                          <PictureOutlined style={{ fontSize: "32px", color: "#8c8c8c" }} />
                          <div style={{ marginTop: "8px", fontWeight: 500, color: "#2246dc" }}>Tải ảnh 1125x90 lên</div>
                          <div className="upload-hint">
                            Kích thước chuẩn: 1125 x 90 px • Dung lượng ≤ 1MB (JPG, PNG) | ≤ 3MB (WEBP, GIF)
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={(e) => handleUploadFile(e, "landscape")}
                            disabled={uploading}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                ) : (
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item
                        label="Nội dung thông điệp *"
                        validateStatus={errors.announcementBarMessage ? "error" : undefined}
                        help={errors.announcementBarMessage}
                      >
                        <Input
                          maxLength={60}
                          showCount
                          placeholder="Nhập nội dung thông điệp ngắn gọn"
                          value={announcementBarMessage}
                          onChange={(e) => setAnnouncementBarMessage(e.target.value)}
                        />
                      </Form.Item>

                      <Form.Item label="CTA">
                        <Input
                          maxLength={18}
                          showCount
                          placeholder="VD: Mua ngay"
                          value={announcementBarCtaText}
                          onChange={(e) => setAnnouncementBarCtaText(e.target.value)}
                        />
                      </Form.Item>

                      <Form.Item label="Icon *">
                        <Radio.Group
                          value={announcementBarIconMode}
                          onChange={(e) => setAnnouncementBarIconMode(e.target.value)}
                          style={{ marginBottom: "12px" }}
                        >
                          <Radio.Button value="NONE">Không</Radio.Button>
                          <Radio.Button value="PRESET">Chọn từ bộ có sẵn</Radio.Button>
                          <Radio.Button value="UPLOAD">Upload</Radio.Button>
                        </Radio.Group>

                        {announcementBarIconMode === "PRESET" && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                            {ICON_PRESETS.map((ic) => (
                              <Button
                                key={ic.code}
                                size="small"
                                type={announcementBarIconPresetCode === ic.code ? "primary" : "default"}
                                onClick={() => setAnnouncementBarIconPresetCode(ic.code)}
                                style={{ borderRadius: "4px" }}
                              >
                                {ic.name}
                              </Button>
                            ))}
                          </div>
                        )}

                        {announcementBarIconMode === "UPLOAD" && (
                          <div>
                            {announcementBarIconURL ? (
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <img
                                  src={announcementBarIconURL}
                                  alt="icon"
                                  style={{ width: "32px", height: "32px", objectFit: "contain" }}
                                />
                                <Button size="small" onClick={() => setAnnouncementBarIconURL("")}>
                                  Xóa
                                </Button>
                              </div>
                            ) : (
                              <label>
                                <Button icon={<UploadOutlined />}>Tải icon lên</Button>
                                <input
                                  type="file"
                                  accept="image/*"
                                  style={{ display: "none" }}
                                  onChange={(e) => handleUploadFile(e, "announcementBarIcon")}
                                />
                              </label>
                            )}
                          </div>
                        )}
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item label="Màu nền *">
                        <ColorPresetPicker
                          backgroundMode={announcementBarBackgroundMode}
                          colorPresetCode={announcementBarColorPresetCode}
                          backgroundColor={announcementBarBackgroundColor}
                          gradientFromColor={announcementBarGradientFromColor}
                          gradientToColor={announcementBarGradientToColor}
                          gradientAngle={announcementBarGradientAngle}
                          textColor={announcementBarTextColor}
                          iconColor={announcementBarIconColor}
                          buttonColor={announcementBarButtonColor}
                          buttonTextColor={announcementBarButtonTextColor}
                          onModeChange={(m) => setAnnouncementBarBackgroundMode(m)}
                          onPresetChange={(p) => {
                            setAnnouncementBarColorPresetCode(p.code);
                            setAnnouncementBarBackgroundColor(p.background);
                            setAnnouncementBarTextColor(p.textColor);
                            setAnnouncementBarButtonColor(p.buttonColor);
                            setAnnouncementBarButtonTextColor(p.buttonTextColor);
                          }}
                          onCustomColorChange={(c) => setAnnouncementBarBackgroundColor(c)}
                          onGradientChange={({ gradientFromColor: gf, gradientToColor: gt, gradientAngle: ga }) => {
                            setAnnouncementBarGradientFromColor(gf);
                            setAnnouncementBarGradientToColor(gt);
                            setAnnouncementBarGradientAngle(ga);
                          }}
                          onElementsColorChange={(colors) => {
                            if (colors.textColor !== undefined) setAnnouncementBarTextColor(colors.textColor);
                            if (colors.iconColor !== undefined) setAnnouncementBarIconColor(colors.iconColor);
                            if (colors.buttonColor !== undefined) setAnnouncementBarButtonColor(colors.buttonColor);
                            if (colors.buttonTextColor !== undefined)
                              setAnnouncementBarButtonTextColor(colors.buttonTextColor);
                          }}
                          onResetDefaultColors={() => {
                            setAnnouncementBarTextColor("#ffffff");
                            setAnnouncementBarIconColor("#ffffff");
                            setAnnouncementBarButtonColor("#ffffff");
                            setAnnouncementBarButtonTextColor("#ee0979");
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                )}
              </div>
            </div>
          </>
        )}

        {/* ========================================================================= */}
        {/* KHU VỰC SẢN PHẨM ÁP DỤNG (KHI CHỌN "ĐẠI LÝ CHƯA TỪNG MUA CÁC SP NÀY")      */}
        {/* ========================================================================= */}
        {sellerSegmentType === SELLER_SEGMENT_TYPE.NOT_PURCHASED_PRODUCT && (
          <ApplicableProductsSection
            products={applicableProducts}
            productIDs={applicableProductIDs}
            onAddProducts={handleAddProducts}
            onRemoveProduct={handleRemoveProduct}
            disabled={isPromotionSource}
            error={errors.applicableProductIDs}
          />
        )}

        {/* ========================================================================= */}
        {/* NÚT THAO TÁC BOTTOM (Khớp 1-1 với Figma)                                  */}
        {/* ========================================================================= */}
        <div className="banner-actions-footer">
          <Button className="btn-cancel" onClick={() => history.push("/marketing-banner")}>
            Hủy bỏ
          </Button>
          <Button type="primary" className="btn-submit" onClick={handlePreSubmit} loading={creating || updating}>
            {isEdit ? "Cập nhật" : "Tạo mới"}
          </Button>
        </div>
      </Form>

      {/* Confirmation Modal */}
      <BannerConfirmModal
        visible={confirmModalVisible}
        isEdit={isEdit}
        type={formType}
        values={currentValues}
        loading={creating || updating}
        onCancel={() => setConfirmModalVisible(false)}
        onConfirm={handleConfirmSubmit}
      />
    </div>
  );
}

export default BannerFormPage;
